import { RECORD_LEASE_FIELD, parseFieldKey } from '@colplat/protocol';
import type { PresenceUser, ServerMessage } from '@colplat/protocol';
import type { OutboxDocumentWrite } from '@colplat/core-client';
import { escapeHtml, presenceDotHtml } from '@colplat/ui-web';
import type { WebledgerClient } from '../webledgerClient.js';
import type { TransactionDto } from './webledgerOfflineState.js';

/**
 * Who is in a transaction right now, tracked once and shared by every page
 * that lists transactions.
 *
 * The register and the reports both need to say "Robert is editing this" and
 * both need to keep saying it as holds come and go: seeding from the list
 * response (nothing replays a hold taken before the page loaded) and
 * re-subscribing on every reconnect (the server's fan-out registry is
 * per-connection).
 *
 * A hold no longer lapses (ADR-0003, 2026-10-22), so there is nothing to
 * sweep: it is held until `leaseFreed` says otherwise, and `leaseTaken`
 * carries whether its holder is connected — "editing now" — or not —
 * "unfinished since …".
 *
 * Two implementations of that would be two sets of those bugs, and the
 * second one would be found by a user rather than by whoever wrote the first.
 */

export interface OwnerInfo { user: PresenceUser; since: string | null }

export class RecordStateTracker {
  /** Held, by a connected tab — "somebody is editing this now". */
  readonly lockedBy = new Map<string, PresenceUser>();
  /** Held at all, connected or not — "somebody's edit", and since when. */
  readonly ownedBy = new Map<string, OwnerInfo>();
  private subscribedIds: string[] = [];

  constructor(
    private readonly client: WebledgerClient,
    /** Called whenever anything changed, so the host can repaint. */
    private readonly onChange: () => void,
  ) {}

  /**
   * Seed from a list response. Nothing replays a lock taken before this page
   * loaded, so the initial state has to come from the server or the list
   * starts out blind to it.
   */
  seed(transactions: ReadonlyArray<{ id: string; lockedBy?: PresenceUser | null; ownedSince?: string | null; ownedBy?: PresenceUser | null }>): void {
    this.lockedBy.clear();
    this.ownedBy.clear();
    for (const tx of transactions) {
      if (tx.ownedBy) this.ownedBy.set(tx.id, { user: tx.ownedBy, since: tx.ownedSince ?? null });
      if (tx.lockedBy) this.lockedBy.set(tx.id, tx.lockedBy);
    }
  }

  /**
   * Plain `subscribe`, deliberately not `session.openRecord` — this is a page
   * watching many rows, not a viewer occupying one. `openRecord` would also
   * join presence, making us look like we had all 250 transactions open.
   *
   * Re-sent on every reconnect because the fan-out registry is per-connection:
   * a new socket starts with none. And driven off transport state rather than
   * called once at mount, because `send()` silently returns false until
   * `welcome` has arrived, which the page's REST fetches routinely beat.
   */
  subscribe(ids: readonly string[]): void {
    if (this.client.transport.state !== 'open') return;
    const added = ids.filter((id) => !this.subscribedIds.includes(id));
    if (added.length === 0) return;
    if (this.client.transport.send({ t: 'subscribe', entity: 'transaction', recordIds: added })) {
      // The server answers a subscribe with the holds standing on those rows
      // (services/sync), so what we believed about them — possibly from
      // before a dropped connection — is replaced, not merged.
      for (const id of added) { this.lockedBy.delete(id); this.ownedBy.delete(id); }
      this.subscribedIds = [...ids];
    }
  }

  /** Forget what we believe we subscribed to — after a reconnect, when the server has forgotten too. */
  resetSubscriptions(): void { this.subscribedIds = []; }

  /** Feed it every server message; it ignores what it does not care about. */
  handleMessage(msg: ServerMessage): void {
    if (msg.t !== 'leaseTaken' && msg.t !== 'leaseFreed') return;
    const { entity, recordId, field } = parseFieldKey(msg.key);
    if (entity !== 'transaction' || field !== RECORD_LEASE_FIELD) return;

    if (msg.t === 'leaseFreed') {
      // A hold now ends only by being released (or taken over, which is a
      // `leaseTaken`), so this always means the record is free.
      this.lockedBy.delete(recordId);
      this.ownedBy.delete(recordId);
    } else {
      const who = this.client.session.presence('transaction', recordId).find((p) => p.id === msg.holder)
        ?? (this.lockedBy.get(recordId)?.id === msg.holder ? this.lockedBy.get(recordId) : undefined)
        ?? (this.ownedBy.get(recordId)?.user.id === msg.holder ? this.ownedBy.get(recordId)?.user : undefined)
        ?? { id: msg.holder, name: 'Someone else', initials: '?', colorHex: '#8a8f98' };
      this.ownedBy.set(recordId, { user: who, since: msg.since });
      if (msg.online) this.lockedBy.set(recordId, who); else this.lockedBy.delete(recordId);
    }
    this.onChange();
  }

  dispose(): void { /* nothing to stop: holds announce every change */ }
}

// ---------------------------------------------------------------- display

export interface RecordState {
  /** Class for the row or card. */
  cls: string;
  /** The holder's identity colour, or '' to leave the border its default. */
  color: string;
  /** Avatar, name and glyph — empty when there is nothing to say. */
  chip: string;
}

const LOCK_ICON = '<svg viewBox="0 0 16 16" width="11" height="11" aria-hidden="true" style="vertical-align:-1px"><path d="M4.5 7V5a3.5 3.5 0 0 1 7 0v2" fill="none" stroke="currentColor" stroke-width="1.5"/><rect x="3" y="7" width="10" height="7" rx="1.5"/></svg>';
/** A cloud with an up arrow: queued on this device, not yet on the server. */
const QUEUE_ICON = '<svg viewBox="0 0 16 16" width="11" height="11" aria-hidden="true" style="vertical-align:-1px"><path d="M4.5 12a3 3 0 0 1 .3-6 4 4 0 0 1 7.6 1 2.5 2.5 0 0 1-.4 5H4.5Z" fill="none" stroke="currentColor" stroke-width="1.3"/><path d="M8 13V8.2m0 0L6.4 9.8M8 8.2l1.6 1.6" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>';
/** A pencil, for the weaker "unfinished edit" state — a different glyph, not a differently-coloured lock (invariant #11). */
const DRAFT_ICON = '<svg viewBox="0 0 16 16" width="11" height="11" aria-hidden="true" style="vertical-align:-1px"><path d="M11.5 1.5 14 4l-8 8-3.2.7.7-3.2 8-8Z" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/></svg>';

export interface RecordStateInput {
  tx: Pick<TransactionDto, 'id' | 'pendingSync'>;
  holder: PresenceUser | undefined;
  owner: OwnerInfo | undefined;
  held: OutboxDocumentWrite | undefined;
  queuedEdit: boolean;
  /**
   * The server's reason, when it would not accept this entry's unsent
   * version as it is (ADR-0011, 2026-10-21). Absent otherwise.
   */
  refused?: string | undefined;
}

/**
 * One record's state, as a chip and a border colour, for a row or a card.
 *
 * The order is a precedence, and it is the interesting part. Work of yours
 * awaiting a decision beats everything, because it is the only state that
 * needs *you* specifically and the only one that will not resolve itself. A
 * live lease beats the durable marker, because "typing right now" is the
 * stronger and more perishable fact. The marker is last and deliberately
 * carries a different glyph rather than a paler lock: "someone is in here"
 * and "someone left this half-done" call for different reactions.
 */
export function recordState({ tx, holder, owner, held, queuedEdit, refused }: RecordStateInput): RecordState {
  const chip = (cls: string, body: string): string => `<span class="wl-state-chip ${cls}">${body}</span>`;

  if (held?.held) {
    return {
      cls: 'wl-tx-held', color: '',
      chip: chip('wl-state-chip--held',
        `<span aria-hidden="true">⚠</span><span class="wl-state-chip__txt">your unsaved edit · ${escapeHtml(held.held.takenByName)} has it</span>`),
    };
  }
  /*
   * Next in line for the same reason: work of yours, stuck, that will not
   * move until you do something. Not "waiting to sync" — it will never sync
   * as it is — and the reason is the server's, in its own words.
   */
  if (refused !== undefined) {
    const says = `Not accepted by the server: ${refused}`;
    return {
      cls: 'wl-tx-refused', color: '',
      chip: `<span class="wl-state-chip wl-state-chip--refused" title="${escapeHtml(says)}" aria-label="${escapeHtml(says)}">`
        + '<span aria-hidden="true">⚠</span><span class="wl-state-chip__txt">not accepted</span></span>',
    };
  }
  if (queuedEdit) {
    return {
      cls: 'wl-tx-pending', color: '',
      chip: chip('wl-state-chip--queued', `${QUEUE_ICON}<span class="wl-state-chip__txt">your edit, waiting to sync</span>`),
    };
  }
  if (tx.pendingSync) {
    return {
      cls: 'wl-tx-pending', color: '',
      chip: chip('wl-state-chip--queued', `${QUEUE_ICON}<span class="wl-state-chip__txt">waiting to sync</span>`),
    };
  }
  if (holder) {
    return {
      cls: 'is-open', color: holder.colorHex,
      chip: chip('', `${presenceDotHtml(holder)}<span class="wl-state-chip__txt">${escapeHtml(holder.name)}</span>${LOCK_ICON}`),
    };
  }
  if (owner) {
    return {
      cls: 'is-open', color: owner.user.colorHex,
      chip: chip('', `${presenceDotHtml(owner.user)}<span class="wl-state-chip__txt">${escapeHtml(owner.user.name)} · unfinished</span>${DRAFT_ICON}`),
    };
  }
  return { cls: '', color: '', chip: '' };
}

/** Every class `recordState` can return, so a repaint can clear the others. */
export const STATE_CLASSES = ['wl-tx-held', 'wl-tx-refused', 'wl-tx-pending', 'is-open'] as const;

/**
 * Put a record's state on a row: its classes, its colour, and its chip in
 * the row's `[data-state-slot]`.
 *
 * Toggled class by class, never assigned. The account register used to set
 * `className` outright, which took every other class on the row with it —
 * the "you are here" marker included, the moment anybody opened anything.
 */
export function applyRecordState(row: HTMLElement, state: RecordState): void {
  const applied = new Set(state.cls.split(' ').filter(Boolean));
  for (const name of STATE_CLASSES) row.classList.toggle(name, applied.has(name));
  if (state.color) row.style.setProperty('--row-color', state.color);
  else row.style.removeProperty('--row-color');
  const slot = row.querySelector<HTMLElement>('[data-state-slot]');
  if (slot) slot.innerHTML = state.chip;
}
